/* StartHR UI kit — React components reproducing the prototype markup/classes
 * (styles in src/styles/style.css). Keep class names identical to the prototype. */
import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes,
} from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { useCountry } from "../lib/country";
import { fmtN, gDate, hash, hDate, initials } from "../lib/format";
import { daysTo } from "../domain/dates";
import { ICONS } from "./iconPaths";

const cx = (...c: (string | false | null | undefined | 0)[]) => c.filter(Boolean).join(" ");

/* ---------- Icon ---------- */
const FLIP = /^(chevron-right|chevron-left|log-out|logout|log-in)$/;
export function Icon({ name, className, title }: { name: string; className?: string; title?: string }) {
  return (
    <svg className={cx("ico", className, FLIP.test(name) && "flip")} viewBox="0 0 24 24" aria-hidden={title ? undefined : true} role={title ? "img" : undefined} aria-label={title}>
      <path d={ICONS[name] || ICONS.info} />
    </svg>
  );
}

/* ---------- Buttons ---------- */
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "pri" | "gold" | "ghost" | "danger" | "sm" | string; icon?: string; end?: string; loading?: boolean };
export function Button({ variant, icon, end, loading, className, children, type = "button", disabled, ...rest }: BtnProps) {
  return (
    <button type={type} className={cx("btn", variant, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="spin" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
      {children != null && children !== "" && <span>{children}</span>}
      {end && <Icon name={end} className="s" />}
    </button>
  );
}
export function IconButton({ icon, label, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon?: string; label: string }) {
  return (
    <button type="button" className={cx("ibtn", className)} aria-label={label} title={label} {...rest}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

/* ---------- Layout ---------- */
export function PageHeader({ title, sub, actions, back }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; back?: { to: string; label: string } }) {
  return (
    <>
      {back && <Link className="back" to={back.to}><Icon name="chevron-left" className="s" /> {back.label}</Link>}
      <div className="ph">
        <div><h1>{title}</h1>{sub && <p>{sub}</p>}</div>
        <div className="ph-actions">{actions}</div>
      </div>
    </>
  );
}

export function Card({ title, sub, actions, children, flush, className, style, testId }: { title?: ReactNode; sub?: ReactNode; actions?: ReactNode; children?: ReactNode; flush?: boolean; className?: string; style?: React.CSSProperties; testId?: string }) {
  return (
    <section className={cx("card", className)} style={style} data-testid={testId}>
      {title != null && (
        <div className="card-h">
          <div><h3>{title}</h3>{sub && <p>{sub}</p>}</div>
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      <div className={cx("card-b", flush && "flush")}>{children}</div>
    </section>
  );
}

export function Kpi({ label, value, unit, icon = "chart", tone, foot, to, testId }: { label: ReactNode; value: ReactNode; unit?: ReactNode; icon?: string; tone?: string; foot?: ReactNode; to?: string; testId?: string }) {
  const inner = (
    <>
      <div className="kpi-top"><span>{label}</span><span className={cx("kpi-ico", tone)}><Icon name={icon} /></span></div>
      <div className="kpi-v">{value}{unit != null && <span className="kpi-u">{unit}</span>}</div>
      <div className="kpi-f">{foot}</div>
    </>
  );
  return to ? <Link className="card kpi" to={to} data-testid={testId}>{inner}</Link> : <div className="card kpi" data-testid={testId}>{inner}</div>;
}

export const Grid = ({ cols, children, className }: { cols: "2" | "3" | "4" | "2-1" | "1-2"; children: ReactNode; className?: string }) => <div className={cx("g", "g-" + cols, className)}>{children}</div>;

/* ---------- Status chips ---------- */
export const TONE: Record<string, string> = {
  Active: "ok", Approved: "ok", Paid: "ok", Present: "ok", Valid: "ok", Compliant: "ok", Submitted: "ok", Settled: "ok", Issued: "ok", Connected: "ok", "Checked in": "ok", Verified: "ok", online: "ok", done: "ok", Reimbursed: "ok",
  Pending: "warn", Draft: "warn", Late: "warn", Expiring: "warn", Due: "warn", "Pending approval": "warn", Probation: "warn", pending: "warn", queued: "warn", "Missing punch": "warn", "Not in yet": "info", trial: "info",
  Repaying: "info", Rejected: "bad", Absent: "bad", Overdue: "bad", offline: "bad", failed: "bad", expired: "bad", Terminated: "", "On leave": "info", Weekend: "", Ready: "info", sent: "info", active: "ok",
};
export function Chip({ status, tone, children, nodot, testId }: { status?: string; tone?: string; children?: ReactNode; nodot?: boolean; testId?: string }) {
  const { t } = useI18n();
  const tn = tone ?? (status ? TONE[status] ?? "" : "");
  return <span className={cx("chip", tn, !nodot && "dot")} data-testid={testId}>{children ?? (status ? t(status) : null)}</span>;
}
export function ExpiryChip({ date, today }: { date: string; today: string }) {
  const { L, t } = useI18n();
  const n = daysTo(today, date);
  if (n < 0) return <span className="chip bad dot">{L("Overdue " + -n + "d", "منتهية منذ " + -n + " يوم")}</span>;
  if (n <= 30) return <span className="chip orange dot">{L(n + " days", n + " يوم")}</span>;
  if (n <= 60) return <span className="chip warn dot">{L(n + " days", n + " يوم")}</span>;
  if (n <= 90) return <span className="chip info dot">{L(n + " days", n + " يوم")}</span>;
  return <span className="chip ok dot">{t("Valid")}</span>;
}

/* ---------- Money & dates ---------- */
/** An amount in the company's currency (useCountry): "SAR 1,234.50", "1,234.500 د.ك". `dec` defaults to the currency's decimals. */
export function Money({ value, dec, plain }: { value: number; dec?: number; plain?: boolean }) {
  const { isAr } = useI18n();
  const c = useCountry();
  const num = (value < 0 ? "−" : "") + fmtN(Math.abs(value), dec ?? c.currency.decimals);
  if (plain) return <span className="money">{num}</span>;
  return isAr
    ? <span className="money" dir="ltr">{num} <span className="cur">{c.currency.symbol[1]}</span></span>
    : <span className="money"><span className="cur">{c.currency.code}</span> {num}</span>;
}
export function DualDate({ date, inline }: { date: string; inline?: boolean }) {
  const { lang, isAr } = useI18n();
  return (
    <span className="dual" style={inline ? { flexDirection: "row", gap: 8, flexWrap: "wrap" } : undefined}>
      <span className="g" dir={isAr ? "rtl" : "ltr"}>{gDate(date, lang)}</span>
      <span className="hj" dir="rtl">{hDate(date)}</span>
    </span>
  );
}

/* ---------- People ---------- */
export function Avatar({ name, size = "" }: { name: string; size?: string }) {
  return <span className={cx("avatar", "t" + (hash(name) % 4), size)} aria-hidden="true">{initials(name)}</span>;
}
export function Person({ e, meta, size = "sm" }: { e: { nameEn: string; nameAr: string; code?: string; title?: string }; meta?: ReactNode; size?: string }) {
  const i = useI18n();
  return (
    <div className="person">
      <Avatar name={e.nameEn} size={size} />
      <div><span className="nm">{i.name(e)}</span><span className="mt2">{meta ?? (e.code ? e.code + " · " + i.title(e.title || "") : "")}</span></div>
    </div>
  );
}

/* ---------- Tabs / segmented ---------- */
export function Tabs<K extends string>({ items, value, onChange, label }: { items: [K, ReactNode, (number | null)?][]; value: K; onChange: (k: K) => void; label?: string }) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map(([k, l, c]) => (
        <button key={k} type="button" role="tab" aria-selected={k === value} className={cx("tab", k === value && "on")} onClick={() => onChange(k)} data-testid={"tab-" + k}>
          {l}{c != null && <> <span className="cnt">{c}</span></>}
        </button>
      ))}
    </div>
  );
}
export function Seg<K extends string>({ items, value, onChange, label }: { items: [K, ReactNode][]; value: K; onChange: (k: K) => void; label?: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {items.map(([k, l]) => <button key={k} type="button" className={k === value ? "on" : ""} aria-pressed={k === value} onClick={() => onChange(k)}>{l}</button>)}
    </div>
  );
}

/* ---------- Forms ---------- */
export function Field({ label, ar, req, err, hint, children, className, htmlFor }: { label: ReactNode; ar?: string; req?: boolean; err?: ReactNode; hint?: ReactNode; children: ReactNode; className?: string; htmlFor?: string }) {
  return (
    <div className={cx("field", className)}>
      <label className="flabel" htmlFor={htmlFor}>
        <span>{label}{req && <> <span className="req">*</span></>}</span>
        {ar && <span className="ar" dir="rtl">{ar}</span>}
      </label>
      {children}
      {err ? <span className="err" role="alert"><Icon name="alert" className="s" />{err}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}
export function Input({ bad, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { bad?: boolean }) {
  return <input className={cx("inp", bad && "bad", className)} aria-invalid={bad || undefined} {...rest} />;
}
export function Select({ options, className, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { options: (string | [string | number, string])[] }) {
  return (
    <select className={cx("sel", className)} {...rest}>
      {options.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <option key={String(v)} value={v}>{l}</option>; })}
    </select>
  );
}
export function Affix({ pre, post, children }: { pre?: ReactNode; post?: ReactNode; children: ReactNode }) {
  return <div className="affix">{pre != null && <span>{pre}</span>}{children}{post != null && <span className="end">{post}</span>}</div>;
}
/** Labelled input in one go (ids wired for accessibility). */
export function TextField({ label, value, onChange, err, hint, req, type, placeholder, dir, mode, testId, ar, className, readOnly }: { label: ReactNode; value: string | number; onChange: (v: string) => void; err?: string; hint?: ReactNode; req?: boolean; type?: string; placeholder?: string; dir?: "ltr" | "rtl"; mode?: InputHTMLAttributes<HTMLInputElement>["inputMode"]; testId?: string; ar?: string; className?: string; readOnly?: boolean }) {
  const id = useId();
  return (
    <Field label={label} req={req} err={err} hint={hint} htmlFor={id} ar={ar} className={className}>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} bad={!!err} type={type} placeholder={placeholder} dir={dir} inputMode={mode} data-testid={testId} readOnly={readOnly} />
    </Field>
  );
}
export function SelectField({ label, value, onChange, options, err, hint, req, testId, className }: { label: ReactNode; value: string | number; onChange: (v: string) => void; options: (string | [string | number, string])[]; err?: string; hint?: ReactNode; req?: boolean; testId?: string; className?: string }) {
  const id = useId();
  return (
    <Field label={label} req={req} err={err} hint={hint} htmlFor={id} className={className}>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} options={options} data-testid={testId} />
    </Field>
  );
}
/** On/off switch. `bare` renders a `<span className="switch">` wrapper instead of a `<label>`,
 *  so the switch can be nested inside another `<label>` (nested labels are invalid HTML). */
export function Toggle({ checked, onChange, label, testId, bare, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; testId?: string; bare?: boolean; disabled?: boolean }) {
  const input = <input type="checkbox" role="switch" checked={checked} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />;
  return bare ? <span className="switch">{input}</span> : <label className="switch">{input}</label>;
}
export function SearchBox({ value, onChange, placeholder, testId }: { value: string; onChange: (v: string) => void; placeholder: string; testId?: string }) {
  return (
    <div className="search"><Icon name="search" /><input className="inp" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} data-testid={testId} /></div>
  );
}

/* ---------- Feedback ---------- */
export function Alert({ tone, title, desc, action, tint, testId }: { tone: "ok" | "info" | "warn" | "bad"; title: ReactNode; desc?: ReactNode; action?: ReactNode; tint?: boolean; testId?: string }) {
  return (
    <div className={cx("alert", tone, tint && "tint")} role={tone === "bad" ? "alert" : "status"} data-testid={testId}>
      <span className="ai"><Icon name={tone === "ok" ? "check" : tone === "info" ? "info" : "alert"} /></span>
      <div className="grow"><div className="alert-t">{title}</div>{desc && <div className="alert-d">{desc}</div>}</div>
      {action}
    </div>
  );
}
export function Bar({ value, max, tone }: { value: number; max: number; tone?: string }) {
  return <div className={cx("bar", tone)} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}><i style={{ width: Math.max(0, Math.min(100, (value / (max || 1)) * 100)) + "%" }} /></div>;
}
export function EmptyState({ title, desc, action }: { title: ReactNode; desc?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty" data-testid="empty">
      <svg className="ill" viewBox="0 0 120 90" aria-hidden="true"><rect x="14" y="18" width="70" height="56" rx="10" fill="var(--sunken)" /><rect x="24" y="30" width="38" height="6" rx="3" fill="var(--border)" /><rect x="24" y="42" width="50" height="6" rx="3" fill="var(--border)" /><rect x="24" y="54" width="28" height="6" rx="3" fill="var(--border)" /><circle cx="84" cy="58" r="16" fill="var(--primary-soft)" /><circle cx="82" cy="56" r="8" fill="none" stroke="var(--primary)" strokeWidth="3" /><path d="M88 62l8 8" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" /></svg>
      <h4>{title}</h4>{desc && <div>{desc}</div>}{action}
    </div>
  );
}
export function Kv({ items }: { items: [ReactNode, ReactNode][] }) {
  return <dl className="kv">{items.map(([k, v], i) => <div key={i} style={{ display: "contents" }}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}
export function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading" data-testid="skeleton">
      <div className="skel" style={{ height: 28, width: 240, marginBottom: 10 }} />
      <div className="skel" style={{ height: 14, width: 320, maxWidth: "80%", marginBottom: 24 }} />
      <div className="g g-4 mb">{[1, 2, 3, 4].map((i) => <div key={i} className="skel" style={{ height: 118, borderRadius: 16 }} />)}</div>
      <div className="g g-2-1"><div className="skel" style={{ height: 300, borderRadius: 16 }} /><div className="skel" style={{ height: 300, borderRadius: 16 }} /></div>
    </div>
  );
}
export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { L } = useI18n();
  const msg = error instanceof Error ? error.message : String(error);
  return <Alert tone="bad" title={L("Something went wrong", "حدث خطأ")} desc={msg} action={onRetry && <Button onClick={onRetry} icon="refresh">{L("Retry", "إعادة المحاولة")}</Button>} testId="error" />;
}
export function AiNote({ children }: { children: ReactNode }) {
  return <div className="ainote"><Icon name="sparkle" className="s" /><span>{children}</span></div>;
}

/* ---------- Modal / drawer ---------- */
export function Modal({ title, children, foot, onClose, className, testId }: { title: ReactNode; children: ReactNode; foot?: ReactNode; onClose: () => void; className?: string; testId?: string }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>("input,select,textarea,button:not(.ibtn)") || el;
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "Tab" && el) {
        const f = Array.from(el.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'));
        if (!f.length) return;
        const a = f[0], z = f[f.length - 1];
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.({ preventScroll: true }); };
  }, [onClose]);
  const drawer = className?.includes("drawer");
  return (
    <div className={cx("modal-back", drawer && "drawer")} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} tabIndex={-1} className={cx("modal", className)} role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid={testId}>
        <div className="mh2"><h2 id={titleId}>{title}</h2><IconButton icon="x" label={t("Close")} onClick={onClose} data-testid="modal-close" /></div>
        <div className="mb2">{children}</div>
        {foot && <div className="mf">{foot}</div>}
      </div>
    </div>
  );
}

export function Confirm({ title, body, confirmLabel, danger, onConfirm, onClose, busy }: { title: ReactNode; body: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void; onClose: () => void; busy?: boolean }) {
  const { t } = useI18n();
  return (
    <Modal title={title} onClose={onClose} testId="confirm" foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant={danger ? "danger" : "pri"} onClick={onConfirm} loading={busy} data-testid="confirm-ok">{confirmLabel}</Button></>}>
      {body}
    </Modal>
  );
}

/* ---------- Toasts ---------- */
type ToastItem = { id: number; msg: ReactNode; tone: "ok" | "bad" | "info" };
const ToastCtx = createContext<(msg: ReactNode, tone?: ToastItem["tone"]) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((msg: ReactNode, tone: ToastItem["tone"] = "ok") => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, msg, tone }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 3400);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite" data-testid="toasts">
        {items.map((x) => <div key={x.id} className={cx("toast", x.tone)} role="status"><Icon name={x.tone === "bad" ? "x" : "check"} /><span>{x.msg}</span></div>)}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

/** Converts any thrown error (ApiError incl.) into a user-facing message. */
export function errMsg(e: unknown): string {
  if (e && typeof e === "object" && "fields" in e && (e as { fields?: Record<string, string> }).fields) {
    const f = (e as { fields: Record<string, string> }).fields;
    const k = Object.keys(f);
    if (k.length) return k.map((x) => x + ": " + f[x]).join(" · ");
  }
  return e instanceof Error ? e.message : String(e);
}

/* ---------- Copy-to-clipboard value row ---------- */
export function CopyValue({ label, value, testId, secret }: { label: ReactNode; value: string; testId?: string; secret?: boolean }) {
  const { L } = useI18n();
  const toast = useToast();
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); } catch {
      const ta = document.createElement("textarea"); ta.value = value; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ } ta.remove();
    }
    toast(L("Copied", "تم النسخ"));
  };
  return (
    <div className="copyrow" data-testid={testId}>
      <span className="copy-l">{label}</span>
      <code className={cx("copy-v", secret && "secret")} dir="ltr">{value}</code>
      <IconButton icon="copy" label={L("Copy", "نسخ")} onClick={copy} />
    </div>
  );
}

/* ---------- Steps list (guides) ---------- */
export function Steps({ steps, vertical = true }: { steps: ReactNode[]; vertical?: boolean }) {
  return <ol className={cx("guide-steps", vertical && "v")}>{steps.map((s, i) => <li key={i}><span className="num">{i + 1}</span><span>{s}</span></li>)}</ol>;
}

/* ---------- Charts (SVG) ---------- */
export function LineChart({ data, fmt, tip, label, auto = true, min: min0 = 0, max: max0, ticks: ticks0 }: { data: [string, number][]; fmt: (v: number) => string; tip?: (v: number) => string; label?: string; auto?: boolean; min?: number; max?: number; ticks?: number[] }) {
  if (data.length < 2) return null;
  const W = 640, H = 220, pl = 52, pr = 16, pt = 22, pb = 28;
  let min = min0, max = max0 ?? Math.max(...data.map((d) => d[1])), ticks = ticks0 ?? [];
  if (auto) {
    const vals = data.map((d) => d[1]); const lo = Math.min(...vals), hi = Math.max(...vals);
    let step = Math.pow(10, Math.floor(Math.log10(hi - lo || 1))); step = (hi - lo) / step > 5 ? step * 2 : step;
    min = Math.floor(lo / step) * step - step; max = Math.ceil(hi / step) * step + step / 2; ticks = [];
    for (let tv = min + step; tv <= max; tv += step * (Math.round((max - min) / step) > 6 ? 2 : 1)) ticks.push(tv);
  }
  const x = (i: number) => pl + (i * (W - pl - pr)) / (data.length - 1);
  const y = (v: number) => pt + (1 - (v - min) / (max - min || 1)) * (H - pt - pb);
  const path = data.map((d, i) => (i ? "L" : "M") + x(i).toFixed(1) + " " + y(d[1]).toFixed(1)).join("");
  const last = data[data.length - 1];
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      {ticks.map((tv) => <g key={tv}><line className="grid" x1={pl} x2={W - pr} y1={y(tv)} y2={y(tv)} /><text className="ax" x={pl - 8} y={y(tv) + 4} textAnchor="end">{fmt(tv)}</text></g>)}
      <path className="area" d={path + `L${x(data.length - 1)} ${H - pb}L${pl} ${H - pb}Z`} /><path className="line" d={path} />
      {data.map((d, i) => {
        const isLast = i === data.length - 1;
        return <g key={i}><text className={cx("ax", i % 2 === 1 && !isLast && "opt")} x={x(i)} y={H - 8} textAnchor="middle">{d[0]}</text><circle className={cx("pt", isLast && "end")} cx={x(i)} cy={y(d[1])} r={isLast ? 5 : 3.5} /><rect className="hit" x={x(i) - 18} y={pt} width={36} height={H - pt - pb}><title>{d[0] + " · " + (tip ? tip(d[1]) : d[1])}</title></rect></g>;
      })}
      <text className="lbl" x={x(data.length - 1) - 8} y={y(last[1]) - 12} textAnchor="end">{fmt(last[1])}</text>
    </svg>
  );
}
export function HBars({ data, max }: { data: [ReactNode, number][]; max?: number }) {
  const m = max || Math.max(1, ...data.map((d) => d[1]));
  return (
    <div className="stack" style={{ gap: 14 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div className="row between sm" style={{ marginBottom: 6, flexWrap: "nowrap" }}><span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d[0]}</span><span className="num">{d[1] >= 1000 ? fmtN(d[1], 0) : d[1]}</span></div>
          <Bar value={d[1]} max={m} tone={i === 0 ? "" : i === 1 ? "gold" : i === 2 ? "info" : ""} />
        </div>
      ))}
    </div>
  );
}
export function Donut({ parts, center, sub }: { parts: [string, number, string][]; center: ReactNode; sub: string }) {
  const tot = parts.reduce((a, p) => a + p[1], 0) || 1, r = 52, c = 2 * Math.PI * r;
  let off = 0;
  return (
    <svg viewBox="0 0 140 140" style={{ width: 140, height: 140, flex: "none" }} role="img" aria-label={sub}>
      <circle cx="70" cy="70" r={r} fill="none" stroke="var(--track)" strokeWidth="16" />
      {parts.map((p, i) => { const len = (p[1] / tot) * c; const el = <circle key={i} cx="70" cy="70" r={r} fill="none" stroke={p[2]} strokeWidth="16" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-off} transform="rotate(-90 70 70)"><title>{p[0] + " · " + p[1]}</title></circle>; off += len; return el; })}
      <text x="70" y="68" textAnchor="middle" style={{ font: "600 22px var(--font-mono)", fill: "var(--ink)" }}>{center}</text>
      <text x="70" y="88" textAnchor="middle" style={{ font: "12px var(--font-sans)", fill: "var(--muted)" }}>{sub}</text>
    </svg>
  );
}
export function Gauge({ pct, bands }: { pct: number; bands: { to: number }[] }) {
  const max = 40, cx0 = 140, cy = 118, r = 100;
  const ang = (v: number) => Math.PI * (1 - Math.min(v, max) / max);
  const pt = (v: number, rr: number) => { const a = ang(v); return [cx0 + rr * Math.cos(a), cy - rr * Math.sin(a)]; };
  const cols = ["var(--danger)", "var(--chart-1)", "var(--chart-1)", "var(--chart-1)", "var(--accent)"], ops = [1, 0.45, 0.7, 1, 1];
  let prev = 0;
  const n = pt(pct, r - 26);
  return (
    <svg className="gauge" viewBox="0 0 280 172" role="img" aria-label={"Saudization " + pct.toFixed(1) + "%"}>
      {bands.map((b, i) => { const to = Math.min(b.to, max); const a = pt(prev + 0.4, r), z = pt(to - 0.4, r); prev = to; return <path key={i} d={`M${a[0]} ${a[1]} A${r} ${r} 0 0 1 ${z[0]} ${z[1]}`} stroke={cols[i]} strokeOpacity={ops[i]} strokeWidth="18" fill="none" />; })}
      <line x1={cx0} y1={cy} x2={n[0]} y2={n[1]} stroke="var(--ink)" strokeWidth="3" strokeLinecap="round" /><circle cx={cx0} cy={cy} r="7" fill="var(--ink)" />
      <text x={cx0} y={cy + 46} textAnchor="middle" style={{ font: "600 26px var(--font-mono)" }}>{pct.toFixed(1)}%</text>
    </svg>
  );
}

/* ---------- Data loading wrapper ---------- */
export function Loaded<T>({ q, children }: { q: { data: T | undefined; isLoading: boolean; error: unknown; refetch: () => unknown }; children: (d: T) => ReactNode }) {
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading || q.data === undefined) return <Skeleton />;
  return <>{children(q.data)}</>;
}

export { cx };
